import React, { useEffect, useMemo, useRef, useState } from "react";
import { ticketRef } from "utilities/ticketNumber";
import { createPortal } from "react-dom";
import {
  X,
  Ticket as TicketIcon,
  Plus,
  Link2,
  Check,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { useFunctions } from "hooks/useFunctions";
import { useToast } from "components/Toast";
import CompanyPickerField from "components/CompanyPickerField";
import LinkPickerField from "components/Tickets/LinkPickerField";
import TemplateIntakeForm from "components/Tickets/TemplateIntakeForm";
import FlowPreview from "components/Workflows/FlowPreview";
import { useTicketRequestTypes, useInternalUsers } from "hooks/crm/useTickets";
import {
  TICKET_PRIORITY_OPTIONS,
  TICKET_PRIORITY_LABELS,
  TICKET_CREATE_ENTITY_TYPES,
  TICKET_ENTITY_META,
} from "constants/tickets";
import { isIntakeValueEmpty } from "constants/ticketTemplates";
import { buildIntakeSubmission } from "utilities/intakeSubmission";
import { planStepSpawns, describeSpawnWhen } from "utilities/conditionalSteps";
import ConfirmDialog from "components/ConfirmDialog";
import { renderTemplateText, buildRenderIntake } from "utilities/ticketTemplateText";
import {
  nextPrefillValue,
  nextPriority,
  isCreateFormDirty,
  closeRequestAction,
} from "utilities/ticketCreateForm";
import {
  shouldShowVariantPicker,
  initialVariantSelection,
  reconcileVariantSelection,
  selectedVariant,
  templateIdForCreate,
  variantOptionLabel,
  variantDisplayLabel,
  carryOverIntake,
} from "utilities/ticketVariants";
import { describeTemplateError } from "utilities/workflowVariants";

const STEP_LABELS = { base: "Basics", intake: "Intake", review: "Review" };

// Same fallback chain the ticket workspace + watcher picker use, so one person
// is labelled identically everywhere.
function displayNameOf(u = {}) {
  return (
    u.name ||
    [u.firstName, u.lastName].filter(Boolean).join(" ") ||
    u.email ||
    "User"
  );
}

/**
 * TicketCreateModal (SOUND-493 → v3 SOUND-510)
 * -------------------------------------------------------
 * A small PAGED WIZARD (CPO decision over a wide multi-column modal). The old
 * single-column modal grew too tall — Create fell below the fold and the MID
 * picker dumped results inline. Fixed frame: header + stepper (fixed) · a
 * scrolling body · a fixed footer, so Back / Next / Create are ALWAYS visible.
 *
 * Internal steps:
 *   1. Basics — request type + priority (first), client, title, kind, description
 *   2. Intake — the active template's TemplateIntakeForm (SKIPPED when the type
 *      has no active template → the wizard collapses to 2 steps)
 *   3. Review — a compact summary + FlowPreview ("generates N steps") + Create.
 *      The optional/advanced entity-link picker lives here, not above the form.
 * Clients get a single guided step (no template, no linking) + Submit.
 *
 * `initialEntity` ({ entityType, entityId, label, companyId }) is the entity-
 * first seam (pre-selects+locks the company, pre-links the entity).
 *
 * SOUND-544: the active template's titleTemplate/descriptionTemplate PREFILL
 * title + description (re-rendered as the client and intake answers change)
 * until the agent edits that field by hand; the request type's defaultPriority
 * preselects priority until the agent picks one. Rules: utilities/ticketCreateForm.js.
 *
 * SOUND-785: a request type may have several live workflow VARIANTS. Internal
 * users get a variant picker (Basics) only when more than one is live, with the
 * default preselected; intake, the step preview and the prefill follow the pick,
 * and answers to a question both variants ask carry across a switch. Clients and
 * sub-tasks never see it and never send `templateId` — the server runs the
 * default. Rules: utilities/ticketVariants.js.
 *
 * SOUND-742: an outside click never closes this modal. Close / Cancel / Escape
 * close a clean form at once and ask before discarding a dirty one. One modal,
 * so this covers the inbox, the company Tickets tab and sub-task mode.
 */
export default function TicketCreateModal({
  open,
  onClose,
  onCreated,
  presetCompanyId,
  presetCompanyName,
  initialEntity,
  subtaskParent,
}) {
  const { user } = useAuth();
  const internal = isInternal(user);
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();
  const { requestTypes } = useTicketRequestTypes();
  const { functions } = useFunctions({ enabled: internal });

  // SOUND-593 — sub-task mode. `subtaskParent` ({ ticketId, number, title,
  // companyId, companyName, priority }) turns this into a single-step "New
  // sub-task" form that POSTs to /tickets/:parentId/children instead of
  // /tickets. Request type is deliberately SUPPRESSED: a type with an active
  // template would instantiate its own child flow, producing grandchildren and
  // breaking the single-level nesting the whole model rests on.
  const isSubtask = !!subtaskParent?.ticketId;
  // Depend on PRIMITIVES in the reset effect, not the `subtaskParent` object:
  // callers pass a fresh literal each render, so an object dep would re-run the
  // reset on every keystroke and wipe the form mid-typing.
  const parentPriority = subtaskParent?.priority || "";
  const { users: internalUsers } = useInternalUsers({ enabled: internal && isSubtask });

  const [step, setStep] = useState(0);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [requestType, setRequestType] = useState("");
  const [requestTypeNote, setRequestTypeNote] = useState("");
  const [priority, setPriority] = useState("normal");
  const [kind, setKind] = useState("ticket");
  const [assigneeId, setAssigneeId] = useState(""); // SOUND-593 sub-task mode only
  const [company, setCompany] = useState({ id: "", name: "" });
  const [entityLinks, setEntityLinks] = useState([]); // [{ entityType, entityId, label }]
  const [linkType, setLinkType] = useState("mid");
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // SOUND-544 — once the agent edits a field by hand, the template stops
  // writing to it (and a hand-picked priority beats the type's default).
  const [titleTouched, setTitleTouched] = useState(false);
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const [priorityTouched, setPriorityTouched] = useState(false);
  // SOUND-742 — discard guard. `initialRef` is the form as opened.
  const [discardConfirm, setDiscardConfirm] = useState(false);
  const initialRef = useRef({});

  // The request type's LIVE workflow variants (internal-only — GET
  // /ticket-templates/variants is gated), default first, and the one picked.
  const [variants, setVariants] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [variantsNonce, setVariantsNonce] = useState(0);
  const [carryNote, setCarryNote] = useState("");
  // A refused create, kept on screen (not only a toast) until the next attempt.
  const [createError, setCreateError] = useState(null);
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [intakeValues, setIntakeValues] = useState({});
  const [intakeLabels, setIntakeLabels] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const titleRef = useRef(null);

  // Reset + autofocus on open. Seed the entity-first seam when present.
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setTitle("");
    setDescription("");
    setRequestType("");
    setRequestTypeNote("");
    // SOUND-593 sub-task mode: a sub-task is a `task` by definition, and its
    // priority defaults to the parent's (a sub-task of an urgent ticket is
    // urgent unless stated) — mirroring the backend default.
    setPriority(parentPriority || "normal");
    setKind(isSubtask ? "task" : "ticket");
    setAssigneeId("");
    setCompany({ id: "", name: "" });
    setShowLinkPicker(false);
    setLinkType("mid");
    setEntityLinks(
      initialEntity && initialEntity.entityType && initialEntity.entityId
        ? [
            {
              entityType: initialEntity.entityType,
              entityId: initialEntity.entityId,
              label: initialEntity.label || initialEntity.entityId,
            },
          ]
        : []
    );
    setTitleTouched(false);
    setDescriptionTouched(false);
    setPriorityTouched(false);
    setDiscardConfirm(false);
    setCreateError(null);
    initialRef.current = {
      title: "",
      description: "",
      requestType: "",
      requestTypeNote: "",
      priority: parentPriority || "normal",
      kind: isSubtask ? "task" : "ticket",
      assigneeId: "",
      companyId: "",
      entityLinkCount: initialEntity && initialEntity.entityType && initialEntity.entityId ? 1 : 0,
    };
    const t = setTimeout(() => titleRef.current?.focus(), 40);
    return () => clearTimeout(t);
  }, [open, initialEntity, isSubtask, parentPriority]);

  const dirty = isCreateFormDirty(
    {
      title,
      description,
      requestType,
      requestTypeNote,
      priority,
      kind,
      assigneeId,
      companyId: company.id,
      intakeValues,
      entityLinkCount: entityLinks.length,
    },
    initialRef.current
  );

  // Close / Cancel / Escape all route here (SOUND-742). The backdrop does not.
  const requestClose = () => {
    const action = closeRequestAction({ dirty, submitting });
    if (action === "close") onClose?.();
    else if (action === "confirm") setDiscardConfirm(true);
  };
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      // While the discard dialog is up, its own Escape handler means "keep
      // editing" — this one must not re-open it in the same keystroke.
      if (discardConfirm) return;
      requestCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, discardConfirm]);

  // A new request type starts a new form: answers belong to the old type.
  useEffect(() => {
    if (!open) return;
    setIntakeValues({});
    setIntakeLabels({});
    setFieldErrors({});
    setCarryNote("");
    setCreateError(null);
    setVariants([]);
    setSelectedTemplateId(null);
  }, [open, requestType]);

  // Look up the request type's LIVE variants (internal-only). None → no intake
  // step (the wizard is 2 steps). `variantsNonce` re-fetches after the server
  // says a pick is no longer live; the user's pick survives if it still is.
  useEffect(() => {
    if (!open || isSubtask) return;
    if (!internal || !requestType || !effectiveCompanyId || !user) return;
    let cancelled = false;
    setIntakeLoading(true);
    makeApiRequest(
      "get",
      apiRoutes.listTicketTemplateVariants(requestType),
      { id: user.id, accessToken: user.accessToken },
      effectiveCompanyId
    )
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        const def = res.data?.defaultTemplateId ?? null;
        setVariants(list);
        setSelectedTemplateId((cur) => (cur ? reconcileVariantSelection(cur, list, def) : initialVariantSelection(list, def)));
      })
      .catch(() => {
        if (!cancelled) {
          setVariants([]);
          setSelectedTemplateId(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIntakeLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, internal, isSubtask, requestType, effectiveCompanyId, variantsNonce]);

  const intakeTemplate = useMemo(
    () => selectedVariant(variants, selectedTemplateId),
    [variants, selectedTemplateId]
  );
  const showVariantPicker = shouldShowVariantPicker({ internal, isSubtask, variants });

  const intakeFields = intakeTemplate?.intakeFields || [];
  // SOUND-791 — the `fields` payload Create sends, and visibility read off it,
  // so a field shown here is never one the server drops as hidden.
  const intakeSubmission = useMemo(
    () => buildIntakeSubmission(intakeTemplate?.intakeFields || [], intakeValues),
    [intakeTemplate, intakeValues]
  );
  const isIntakeFieldVisible = (f) => !!f?.key && intakeSubmission.visibleKeys.has(f.key);
  // SOUND-546 — which steps the current answers WILL create. Live: the plan
  // re-evaluates every step's `spawnWhen` as intake changes, against the values
  // the server will store (a hidden field counts as unanswered).
  const stepPlan = useMemo(
    () => planStepSpawns(intakeTemplate?.childSteps || [], intakeTemplate?.intakeFields || [], intakeValues),
    [intakeTemplate, intakeValues]
  );
  const templateStepCount = intakeTemplate?.childSteps?.length || 0;
  const flowStepCount = stepPlan.created.length;
  const hasIntakeStep = internal && !!intakeTemplate && intakeFields.length > 0 && !isSubtask;

  // Wizard steps. Clients get a single guided step (no template, no linking).
  // SOUND-593 sub-task mode is a SINGLE step: no request type (so no template,
  // so no intake), no entity linking (the child inherits the parent's links
  // server-side), and nothing to review that isn't already on screen.
  const stepKeys = isSubtask
    ? ["base"]
    : !internal
    ? ["base"]
    : hasIntakeStep
    ? ["base", "intake", "review"]
    : ["base", "review"];

  // Clamp the active step if the step list shrinks (e.g. request type changed).
  useEffect(() => {
    setStep((s) => Math.min(s, stepKeys.length - 1));
  }, [stepKeys.length]);

  // ── SOUND-544 prefill + default priority ──
  const selectedRequestType = requestTypes.find((rt) => rt.key === requestType) || null;
  const requestTypeLabel = selectedRequestType?.label || requestType || "";
  // Absent on the API row = no default (it is only sent when set).
  const typeDefaultPriority = selectedRequestType?.defaultPriority ?? null;
  const prefillClient =
    subtaskParent?.companyName ||
    presetCompanyName ||
    (initialEntity?.companyId ? initialEntity.label : "") ||
    company.name ||
    "";
  const prefillEnabled = open && internal && !isSubtask;
  const titleTemplate = intakeTemplate?.titleTemplate ?? null;
  const descriptionTemplate = intakeTemplate?.descriptionTemplate ?? null;

  useEffect(() => {
    if (!prefillEnabled) return;
    const ctx = {
      client: prefillClient,
      requestType: requestTypeLabel,
      // {{variant}} — the picked variant's name (SOUND-785); the request-type
      // label for a legacy row with none, as the API renders it.
      variant: intakeTemplate ? variantDisplayLabel(intakeTemplate, requestTypeLabel) : undefined,
      intake: buildRenderIntake(intakeFields.filter(isIntakeFieldVisible), intakeValues, intakeLabels),
    };
    setTitle((cur) =>
      nextPrefillValue({ current: cur, rendered: renderTemplateText(titleTemplate, ctx), touched: titleTouched })
    );
    setDescription((cur) =>
      nextPrefillValue({
        current: cur,
        rendered: renderTemplateText(descriptionTemplate, ctx),
        touched: descriptionTouched,
      })
    );
    // intakeFields is derived from intakeTemplate, which is a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prefillEnabled,
    intakeTemplate,
    titleTemplate,
    descriptionTemplate,
    prefillClient,
    requestTypeLabel,
    intakeValues,
    intakeLabels,
    titleTouched,
    descriptionTouched,
  ]);

  useEffect(() => {
    if (!prefillEnabled) return;
    setPriority((cur) =>
      nextPriority({
        current: cur,
        touched: priorityTouched,
        defaultPriority: typeDefaultPriority,
        fallback: "normal",
        allowed: TICKET_PRIORITY_OPTIONS,
      })
    );
  }, [prefillEnabled, requestType, typeDefaultPriority, priorityTouched]);

  if (!open) return null;

  const isOther = String(requestType).toLowerCase() === "other";
  // A sub-task's client is the PARENT's — inherited server-side and never asked
  // for, so the picker collapses to a read-only row via the existing lock path.
  const lockedCompanyId = subtaskParent?.companyId || presetCompanyId || initialEntity?.companyId || "";
  const lockedCompanyName =
    subtaskParent?.companyName ||
    presetCompanyName ||
    (initialEntity?.companyId ? initialEntity.label : "");
  const resolvedCompanyId = lockedCompanyId || company.id;
  const needsCompany = internal && !lockedCompanyId;
  const authUser = { id: user.id, accessToken: user.accessToken };

  const currentKey = stepKeys[Math.min(step, stepKeys.length - 1)];
  const isFirstStep = step === 0;
  const isLastStep = step >= stepKeys.length - 1;
  const clientDisplay = lockedCompanyName || company.name || resolvedCompanyId || "—";

  const canSubmit =
    !!title.trim() && (!needsCompany || !!resolvedCompanyId) && !submitting;

  const setIntakeValue = (key, value, label) => {
    setIntakeValues((prev) => ({ ...prev, [key]: value }));
    if (label !== undefined) setIntakeLabels((prev) => ({ ...prev, [key]: label }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Switch workflow variant. Answers carry over only where the new variant
  // asks the same question (utilities/ticketVariants.carryOverIntake).
  const changeVariant = (templateId) => {
    const next = selectedVariant(variants, templateId);
    if (!next || templateId === selectedTemplateId) return;
    const { values, labels, dropped } = carryOverIntake({
      fromFields: intakeFields,
      toFields: next.intakeFields || [],
      values: intakeValues,
      labels: intakeLabels,
    });
    const lost = dropped.filter((k) => !isIntakeValueEmpty(intakeValues[k]));
    setIntakeValues(values);
    setIntakeLabels(labels);
    setFieldErrors({});
    setCreateError(null);
    setCarryNote(
      lost.length > 0
        ? `${lost.length} ${lost.length === 1 ? "answer" : "answers"} did not carry over — this variant does not ask ${
            lost.length === 1 ? "that question" : "those questions"
          } the same way.`
        : ""
    );
    setSelectedTemplateId(templateId);
  };

  const addEntityLink = ({ entityId, label }) => {
    setEntityLinks((prev) =>
      prev.some((l) => l.entityType === linkType && l.entityId === entityId)
        ? prev
        : [...prev, { entityType: linkType, entityId, label }]
    );
    setShowLinkPicker(false);
  };
  const removeEntityLink = (idx) => setEntityLinks((prev) => prev.filter((_, i) => i !== idx));

  const buildIntakeFields = () => intakeSubmission.fields;

  const validateIntake = () => {
    const errs = {};
    intakeFields.forEach((f) => {
      if (!isIntakeFieldVisible(f)) return;
      if (f.required && isIntakeValueEmpty(intakeValues[f.key])) errs[f.key] = "This field is required.";
    });
    return errs;
  };

  // ── navigation ──
  // A title template may only produce text once intake is answered, so an
  // empty-but-template-managed title may pass Basics; Create still needs one.
  const titleWillPrefill = !!titleTemplate && !titleTouched && !isSubtask;
  const validateBase = () => {
    if (!title.trim() && !(titleWillPrefill && !isLastStep)) {
      toast({ status: "warning", title: "Add a title" });
      return false;
    }
    if (needsCompany && !resolvedCompanyId) {
      toast({ status: "warning", title: "Select a client" });
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (currentKey === "base") {
      if (!validateBase()) return;
      if (intakeLoading) return; // wait for the template lookup to settle
    }
    if (currentKey === "intake") {
      const errs = validateIntake();
      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
        toast({ status: "warning", title: "Fill in the required intake fields" });
        return;
      }
    }
    setStep((s) => Math.min(s + 1, stepKeys.length - 1));
  };
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!canSubmit) return;
    if (internal && intakeTemplate) {
      const errs = validateIntake();
      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
        toast({ status: "warning", title: "Fill in the required intake fields" });
        return;
      }
    }

    setSubmitting(true);
    setCreateError(null);
    try {
      // SOUND-593 — sub-task mode posts to a DIFFERENT endpoint and a reduced
      // payload. companyId/parentTicketId are derived server-side from the parent
      // (and ignored if sent), the child inherits the parent's entity links, and
      // no requestType is sent so no nested flow can be instantiated.
      if (isSubtask) {
        const res = await makeApiRequest(
          "post",
          apiRoutes.addTicketChild(subtaskParent.ticketId),
          authUser,
          effectiveCompanyId,
          {
            title: title.trim(),
            description: description.trim() || undefined,
            priority,
            kind: "task",
            ...(assigneeId ? { assigneeId } : {}),
          }
        );
        const child = res?.data?.data;
        toast({
          status: "success",
          title: child?.number ? `Sub-task ${ticketRef(child.number)} added` : "Sub-task added",
        });
        onCreated?.(child);
        return;
      }

      const payload = {
        title: title.trim(),
        description: description.trim() || undefined,
        requestType: requestType || undefined,
        ...(isOther && requestTypeNote.trim() ? { requestTypeNote: requestTypeNote.trim() } : {}),
      };
      if (internal) {
        payload.priority = priority;
        payload.kind = kind;
        if (resolvedCompanyId) payload.companyId = resolvedCompanyId;
        if (intakeTemplate) payload.fields = buildIntakeFields();
        // SOUND-785 — only when the picker was shown; omitted = the default.
        const templateId = templateIdForCreate({ internal, isSubtask, variants, selectedTemplateId });
        if (templateId) payload.templateId = templateId;
      }

      const res = await makeApiRequest("post", apiRoutes.createTicket, authUser, effectiveCompanyId, payload);
      const created = res?.data?.data;

      const base = created?.number ? `Created ${ticketRef(created.number)}` : "Ticket created";
      // SOUND-546 — the server's own record of what it skipped wins over the
      // client-side plan when the response carries it.
      const stepsMade = Array.isArray(created?.skippedSteps)
        ? Math.max(0, templateStepCount - created.skippedSteps.length)
        : flowStepCount;
      const successTitle =
        stepsMade > 0 ? `${base} + ${stepsMade} ${stepsMade === 1 ? "step" : "steps"}` : base;

      // Entity links are internal-only follow-up POSTs.
      if (internal && created?.ticketId && entityLinks.length > 0) {
        const results = await Promise.allSettled(
          entityLinks.map((l) =>
            makeApiRequest("post", apiRoutes.addTicketLink(created.ticketId), authUser, effectiveCompanyId, {
              entityType: l.entityType,
              entityId: l.entityId,
              label: l.label,
              role: "related",
            })
          )
        );
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed > 0) {
          toast({
            status: "warning",
            title: "Ticket created, some links failed",
            description: `${failed} of ${entityLinks.length} entity link(s) could not be attached.`,
          });
        } else {
          toast({ status: "success", title: successTitle });
        }
      } else {
        toast({ status: "success", title: successTitle });
      }

      onCreated?.(created);
    } catch (err) {
      const details = err.response?.data?.details;
      if (Array.isArray(details) && details.length > 0) {
        const errs = {};
        details.forEach((msg) => {
          const m = /"([^"]+)"/.exec(msg);
          if (m) errs[m[1]] = msg;
        });
        setFieldErrors((prev) => ({ ...prev, ...errs }));
        // If the failure is in the intake, jump back to that step to fix it.
        if (hasIntakeStep) setStep(stepKeys.indexOf("intake"));
        toast({
          status: "destructive",
          title: "Intake validation failed",
          description: details.slice(0, 3).join(" · "),
        });
      } else {
        const model = describeTemplateError(err, isSubtask ? "Failed to add sub-task" : "Failed to create ticket");
        // A variant code (e.g. the picked variant went out of date while the
        // form was open) stays on screen; the variant list is re-read so the
        // picker offers only what is live now.
        if (model.code) {
          setCreateError(model);
          if (["INVALID_TEMPLATE_FOR_REQUEST_TYPE", "NO_DEFAULT_VARIANT"].includes(model.code)) {
            setVariantsNonce((n) => n + 1);
          }
        }
        toast({
          status: "destructive",
          // Surface the backend copy verbatim — notably "Cannot add a
          // sub-task to a closed ticket — reopen it first".
          title: isSubtask ? "Failed to add sub-task" : "Failed to create ticket",
          description: model.message,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      if (isLastStep) submit();
      else goNext();
    }
  };

  // Template order, with skipped steps kept (muted) so the author of the
  // ticket sees what will NOT be created and why. Created steps carry the
  // dependencies the server keeps — one on a skipped step is dropped.
  const createdByKey = {};
  stepPlan.created.forEach((s) => (createdByKey[s.key] = s));
  const previewSteps = (intakeTemplate?.childSteps || []).map((s) => ({
    _id: s.key,
    title: s.title,
    assigneeRole: s.assigneeRole,
    defaultPriority: s.defaultPriority,
    spawnWhen: s.spawnWhen || null,
    dependsOn: (createdByKey[s.key] || s).dependsOnStepKeys || [],
  }));
  const previewSkippedIds = stepPlan.skipped.map((s) => s.key);
  const previewConditionText = {};
  (intakeTemplate?.childSteps || []).forEach((s) => {
    const text = describeSpawnWhen(s.spawnWhen, intakeFields);
    if (text) previewConditionText[s.key] = text;
  });

  const heading = isSubtask ? "New sub-task" : internal ? "New ticket" : "New request";

  return createPortal(
    // SOUND-742 — no onClick here: an outside click must never discard input.
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="surface flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-xl shadow-lg ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        {/* Header (fixed) */}
        <div className="shrink-0 border-b border-border p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-brand/15 text-brand">
                <TicketIcon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">{heading}</h3>
                {!internal && (
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    Tell us what you need — you can track status anytime.
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={requestClose}
              className="btn btn-ghost h-8 w-8 shrink-0 p-0"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {stepKeys.length > 1 && (
            <div className="mt-4 flex items-center gap-1.5">
              {stepKeys.map((k, i) => (
                <React.Fragment key={k}>
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`grid h-5 w-5 place-items-center rounded-full text-[11px] font-semibold ${
                        i < step
                          ? "bg-brand text-brand-foreground"
                          : i === step
                          ? "bg-brand/15 text-brand ring-1 ring-brand"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {i < step ? <Check className="h-3 w-3" /> : i + 1}
                    </span>
                    <span
                      className={`text-xs font-medium ${
                        i === step ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {STEP_LABELS[k]}
                    </span>
                  </div>
                  {i < stepKeys.length - 1 && <div className="h-px w-5 bg-border" />}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Body (scrolls) */}
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {createError && (
            <div role="alert" className="mb-4 rounded-md border border-destructive/30 bg-destructive/[0.06] px-3 py-2 text-xs">
              <div className="flex items-start justify-between gap-2">
                <span className="font-semibold text-foreground">{createError.title}</span>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => setCreateError(null)}
                >
                  Dismiss
                </button>
              </div>
              <p className="mt-0.5 text-muted-foreground">{createError.message}</p>
            </div>
          )}
          {currentKey === "base" && (
            <div className="space-y-4">
              {internal ? (
                <>
                  {/* Parent context — a sub-task is only meaningful relative to
                      the ticket it belongs to, so name it up front. */}
                  {isSubtask && (
                    <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
                      <span className="text-muted-foreground">Sub-task of </span>
                      <span className="font-mono text-muted-foreground">{ticketRef(subtaskParent.number)}</span>{" "}
                      <span className="text-foreground">{subtaskParent.title}</span>
                    </div>
                  )}

                  {/* Request type + priority FIRST. Request type is suppressed for
                      a sub-task (see the isSubtask note above) — priority then
                      takes the full row, and Assignee joins it. */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {!isSubtask && (
                      <label className="flex flex-col text-sm">
                        <span className="mb-1 font-medium text-foreground">Request type</span>
                        <select
                          className="select"
                          value={requestType}
                          onChange={(e) => setRequestType(e.target.value)}
                        >
                          <option value="">Select…</option>
                          {requestTypes.map((rt) => (
                            <option key={rt.key} value={rt.key}>
                              {rt.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    <label className="flex flex-col text-sm">
                      <span className="mb-1 font-medium text-foreground">Priority</span>
                      <select
                        className="select"
                        value={priority}
                        onChange={(e) => {
                          setPriorityTouched(true);
                          setPriority(e.target.value);
                        }}
                      >
                        {TICKET_PRIORITY_OPTIONS.map((p) => (
                          <option key={p} value={p}>
                            {TICKET_PRIORITY_LABELS[p]}
                          </option>
                        ))}
                      </select>
                    </label>
                    {isSubtask && (
                      <label className="flex flex-col text-sm">
                        <span className="mb-1 font-medium text-foreground">
                          Assignee <span className="text-muted-foreground">(optional)</span>
                        </span>
                        <select
                          className="select"
                          value={assigneeId}
                          onChange={(e) => setAssigneeId(e.target.value)}
                        >
                          <option value="">Unassigned</option>
                          {/* The id field is `_id || id || uid` — `useInternalUsers`
                              returns RAW user docs, which have no `userId`. Getting
                              this wrong is silent and nasty: an <option> with an
                              undefined value falls back to its TEXT CONTENT, so the
                              form cheerfully submits the person's display name as
                              the id and the API answers "Unknown assigneeId". Hence
                              resolving the id up front and dropping any row without
                              one, rather than interpolating in the value prop. */}
                          {internalUsers
                            .map((u) => ({ id: u._id || u.id || u.uid, label: displayNameOf(u) }))
                            .filter((u) => !!u.id)
                            .map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.label}
                              </option>
                            ))}
                        </select>
                      </label>
                    )}
                  </div>

                  {isOther && (
                    <label className="flex flex-col text-sm">
                      <span className="mb-1 font-medium text-foreground">
                        Tell us more <span className="text-muted-foreground">(optional)</span>
                      </span>
                      <input
                        type="text"
                        className="input"
                        placeholder="Briefly describe the request type"
                        value={requestTypeNote}
                        onChange={(e) => setRequestTypeNote(e.target.value)}
                      />
                    </label>
                  )}
                  {/* SOUND-785 — only when 2+ variants are live. */}
                  {showVariantPicker && (
                    <label className="flex flex-col text-sm">
                      <span className="mb-1 font-medium text-foreground">Workflow variant</span>
                      <select
                        className="select"
                        value={selectedTemplateId || ""}
                        onChange={(e) => changeVariant(e.target.value)}
                      >
                        {variants.map((v) => (
                          <option key={v.templateId} value={v.templateId}>
                            {variantOptionLabel(v, requestTypeLabel)}
                          </option>
                        ))}
                      </select>
                      {intakeTemplate?.description && (
                        <span className="mt-1 text-xs text-muted-foreground">{intakeTemplate.description}</span>
                      )}
                      {carryNote && <span className="mt-1 text-xs text-warning">{carryNote}</span>}
                    </label>
                  )}
                  {intakeLoading && (
                    <p className="text-xs text-muted-foreground">Checking for an intake form…</p>
                  )}
                  {!intakeLoading && hasIntakeStep && (
                    <p className="text-xs text-muted-foreground">
                      This request type has an intake form — next step.
                    </p>
                  )}

                  {/* Client */}
                  {lockedCompanyId ? (
                    <div className="flex flex-col text-sm">
                      <span className="mb-1 font-medium text-foreground">Client</span>
                      <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-foreground">
                        {lockedCompanyName || lockedCompanyId}
                      </div>
                    </div>
                  ) : (
                    <CompanyPickerField
                      label="Client"
                      companyType="client"
                      value={company.id}
                      valueLabel={company.name}
                      onChange={setCompany}
                    />
                  )}

                  {/* Title */}
                  <label className="flex flex-col text-sm">
                    <span className="mb-1 font-medium text-foreground">Title</span>
                    <input
                      ref={titleRef}
                      type="text"
                      className="input"
                      placeholder="Short summary of the request"
                      value={title}
                      onChange={(e) => {
                        setTitleTouched(true);
                        setTitle(e.target.value);
                      }}
                    />
                    {titleWillPrefill && (
                      <span className="mt-1 text-xs text-muted-foreground">
                        Filled from the workflow and updated as you answer the intake. Edit it to take over.
                      </span>
                    )}
                  </label>

                  {/* Kind — hidden for a sub-task, which is a `task` by
                      definition (forced on open). */}
                  <div className={`flex flex-col text-sm ${isSubtask ? "hidden" : ""}`}>
                    <span className="mb-1 font-medium text-foreground">Kind</span>
                    <div className="inline-flex w-fit rounded-md border border-border p-0.5">
                      {["ticket", "task"].map((k) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setKind(k)}
                          className={`rounded px-3 py-1 text-xs font-semibold capitalize transition-colors ${
                            kind === k
                              ? "bg-brand/15 text-foreground"
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          {k}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <label className="flex flex-col text-sm">
                    <span className="mb-1 font-medium text-foreground">Description</span>
                    <textarea
                      className="textarea"
                      placeholder="Context, links, and what a good resolution looks like"
                      value={description}
                      onChange={(e) => {
                        setDescriptionTouched(true);
                        setDescription(e.target.value);
                      }}
                    />
                    {!!descriptionTemplate && !descriptionTouched && !isSubtask && (
                      <span className="mt-1 text-xs text-muted-foreground">
                        Filled from the workflow. Edit it to take over.
                      </span>
                    )}
                  </label>
                </>
              ) : (
                <>
                  {/* Client single-step form */}
                  <label className="flex flex-col text-sm">
                    <span className="mb-1 font-medium text-foreground">What do you need?</span>
                    <input
                      ref={titleRef}
                      type="text"
                      className="input"
                      placeholder="e.g. Update our customer service phone number"
                      value={title}
                      onChange={(e) => {
                        setTitleTouched(true);
                        setTitle(e.target.value);
                      }}
                    />
                  </label>
                  <label className="flex flex-col text-sm">
                    <span className="mb-1 font-medium text-foreground">Type of request</span>
                    <select
                      className="select"
                      value={requestType}
                      onChange={(e) => setRequestType(e.target.value)}
                    >
                      <option value="">Select…</option>
                      {requestTypes.map((rt) => (
                        <option key={rt.key} value={rt.key}>
                          {rt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {isOther && (
                    <label className="flex flex-col text-sm">
                      <span className="mb-1 font-medium text-foreground">
                        Tell us more <span className="text-muted-foreground">(optional)</span>
                      </span>
                      <input
                        type="text"
                        className="input"
                        placeholder="Briefly describe the request type"
                        value={requestTypeNote}
                        onChange={(e) => setRequestTypeNote(e.target.value)}
                      />
                    </label>
                  )}
                  <label className="flex flex-col text-sm">
                    <span className="mb-1 font-medium text-foreground">Details</span>
                    <textarea
                      className="textarea"
                      placeholder="Share any details that would help us help you"
                      value={description}
                      onChange={(e) => {
                        setDescriptionTouched(true);
                        setDescription(e.target.value);
                      }}
                    />
                  </label>
                </>
              )}
            </div>
          )}

          {currentKey === "intake" && intakeTemplate && (
            <div>
              <div className="mb-3 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground">
                    {intakeTemplate.name || "Intake"}
                  </div>
                  {intakeTemplate.description && (
                    <p className="mt-0.5 text-xs text-muted-foreground">{intakeTemplate.description}</p>
                  )}
                </div>
                {templateStepCount > 0 && (
                  <span className="badge badge-muted shrink-0">
                    generates {flowStepCount} {flowStepCount === 1 ? "step" : "steps"}
                    {stepPlan.skipped.length > 0 && ` · ${stepPlan.skipped.length} skipped`}
                  </span>
                )}
              </div>
              <TemplateIntakeForm
                fields={intakeFields}
                values={intakeValues}
                labels={intakeLabels}
                errors={fieldErrors}
                onChange={setIntakeValue}
                companyId={resolvedCompanyId}
              />
            </div>
          )}

          {currentKey === "review" && (
            <div className="space-y-5">
              {!title.trim() && (
                <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-foreground">
                  The workflow's title came out empty — go back to Basics and add one before creating.
                </p>
              )}
              {/* Summary */}
              <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-3 text-sm">
                <SummaryRow label="Request type" value={requestTypeLabel || "—"} />
                {showVariantPicker && intakeTemplate && (
                  <SummaryRow label="Variant" value={variantDisplayLabel(intakeTemplate, requestTypeLabel)} />
                )}
                <SummaryRow label="Client" value={clientDisplay} />
                <SummaryRow label="Title" value={title || "—"} />
                <SummaryRow label="Priority" value={TICKET_PRIORITY_LABELS[priority] || priority} />
                <SummaryRow label="Kind" value={kind} className="capitalize" />
              </div>

              {/* Intake summary */}
              {intakeFields.length > 0 && (
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Intake
                  </div>
                  <div className="space-y-1.5 text-sm">
                    {intakeFields
                      .filter(isIntakeFieldVisible)
                      .map((f) => {
                        const raw = intakeValues[f.key];
                        const display =
                          f.type === "mid-ref" || f.type === "company-ref"
                            ? intakeLabels[f.key] || raw
                            : typeof raw === "boolean"
                            ? raw
                              ? "Yes"
                              : "No"
                            : raw;
                        return (
                          <SummaryRow
                            key={f.key}
                            label={f.label || f.key}
                            value={isIntakeValueEmpty(display) ? "—" : String(display)}
                          />
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Flow preview */}
              {intakeTemplate && previewSteps.length > 0 && (
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    This generates
                  </div>
                  <FlowPreview
                    requestTypeLabel={requestTypeLabel || "Ticket"}
                    intakeFields={intakeFields}
                    steps={previewSteps}
                    functions={functions}
                    conditionTextById={previewConditionText}
                    skippedIds={previewSkippedIds}
                  />
                </div>
              )}

              {/* Linked entities (optional/advanced). Hidden for a sub-task —
                  it inherits the parent's links server-side, so offering a
                  picker here would imply the child can diverge. */}
              {internal && !isSubtask && (
                <div>
                  <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <Link2 className="h-3.5 w-3.5" />
                    Linked entities <span className="font-normal normal-case">(optional)</span>
                  </div>
                  {entityLinks.length > 0 && (
                    <ul className="mb-2 space-y-1.5">
                      {entityLinks.map((l, i) => (
                        <li
                          key={`${l.entityType}:${l.entityId}`}
                          className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5"
                        >
                          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            {TICKET_ENTITY_META[l.entityType]?.label || l.entityType}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                            {l.label || l.entityId}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeEntityLink(i)}
                            className="text-muted-foreground hover:text-destructive"
                            aria-label="Remove link"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {showLinkPicker ? (
                    <div className="space-y-2 rounded-md border border-border bg-muted/20 p-2.5">
                      <div className="flex items-center justify-between">
                        <select
                          className="select h-8 w-auto text-sm"
                          value={linkType}
                          onChange={(e) => setLinkType(e.target.value)}
                        >
                          {TICKET_CREATE_ENTITY_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {TICKET_ENTITY_META[t].label}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn-ghost h-7 px-2 text-xs"
                          onClick={() => setShowLinkPicker(false)}
                        >
                          Done
                        </button>
                      </div>
                      <LinkPickerField
                        entityType={linkType}
                        companyId={resolvedCompanyId}
                        onSelect={addEntityLink}
                        placeholder={`Search ${TICKET_ENTITY_META[linkType].label.toLowerCase()}…`}
                      />
                    </div>
                  ) : (
                    <button type="button" className="btn btn-outline w-fit" onClick={() => setShowLinkPicker(true)}>
                      <Plus className="h-4 w-4" />
                      Attach entity
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer (fixed — Create is always visible) */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border p-4">
          <button type="button" className="btn btn-ghost" onClick={requestClose} disabled={submitting}>
            Cancel
          </button>
          <div className="flex items-center gap-2">
            {!isFirstStep && (
              <button type="button" className="btn btn-outline" onClick={goBack} disabled={submitting}>
                <ChevronLeft className="h-4 w-4" />
                Back
              </button>
            )}
            {isLastStep ? (
              <button type="button" className="btn btn-primary" onClick={submit} disabled={!canSubmit}>
                {isSubtask ? "Create sub-task" : internal ? "Create ticket" : "Submit request"}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                onClick={goNext}
                disabled={currentKey === "base" && intakeLoading}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={discardConfirm}
        title={isSubtask ? "Discard this sub-task?" : internal ? "Discard this ticket?" : "Discard this request?"}
        description="What you've entered will be lost."
        confirmLabel="Discard"
        variant="destructive"
        onCancel={() => setDiscardConfirm(false)}
        onConfirm={() => {
          setDiscardConfirm(false);
          onClose?.();
        }}
      />
    </div>,
    document.body
  );
}

function SummaryRow({ label, value, className = "" }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className={`min-w-0 truncate text-right font-medium text-foreground ${className}`}>{value}</span>
    </div>
  );
}
